
import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Check, X } from "lucide-react";
import { Region } from "@/types/region";

interface RegionManagementProps {
  regions: Region[] | undefined;
  refetchRegions: () => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const RegionManagement = ({ 
  regions, 
  refetchRegions, 
  isOpen, 
  setIsOpen 
}: RegionManagementProps) => {
  const { toast } = useToast();
  const [newRegionCode, setNewRegionCode] = useState("");
  const [newRegionName, setNewRegionName] = useState("");

  const handleAddRegion = async () => {
    if (!newRegionCode || !newRegionName) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in both code and name fields.",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("regions")
        .insert([{ code: newRegionCode.toLowerCase(), name: newRegionName }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Region added successfully.",
      });

      setNewRegionCode("");
      setNewRegionName("");
      setIsOpen(false);
      refetchRegions();
    } catch (error) {
      console.error('Error adding region:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add region. It might already exist.",
      });
    }
  };

  const handleToggleRegionStatus = async (region: Region) => {
    try {
      const { error } = await supabase
        .from("regions")
        .update({ active: !region.active })
        .eq("id", region.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Region ${region.name} ${!region.active ? 'activated' : 'deactivated'} successfully.`,
      });

      refetchRegions();
    } catch (error) {
      console.error('Error toggling region status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update region status.",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="hidden">
          Manage Regions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Regions</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="regionCode">Region Code</Label>
              <Input
                id="regionCode"
                placeholder="e.g., mombasa"
                value={newRegionCode}
                onChange={(e) => setNewRegionCode(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="regionName">Region Name</Label>
              <Input
                id="regionName"
                placeholder="e.g., Mombasa"
                value={newRegionName}
                onChange={(e) => setNewRegionName(e.target.value)}
              />
            </div>
            <Button onClick={handleAddRegion}>Add Region</Button>
          </div>
          
          <div className="mt-6">
            <h3 className="text-sm font-medium mb-2">Existing Regions</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {regions?.map((region) => (
                  <TableRow key={region.id}>
                    <TableCell>{region.code}</TableCell>
                    <TableCell>{region.name}</TableCell>
                    <TableCell>
                      {region.active ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <X className="h-4 w-4 text-red-500" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleRegionStatus(region)}
                      >
                        {region.active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
